/**
 * Finance Dashboard Page
 * Overview of all accounts' license utilization and billing insights
 */

import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import {
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Building2,
  DollarSign,
  TrendingDown,
  Activity,
  Target,
  AlertCircle,
  CheckCircle,
  Download,
  SlidersHorizontal,
  Wallet,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAccountUsage } from '@/hooks/useFinanceApi'
import { STATUS_CONFIG } from '@/features/finance/types'
import type { UsageStatus } from '@/features/finance/types'
import type { AccountUsageData } from '@/api/types'

// Format large numbers
function formatNumber(num: number): string {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + 'B'
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M'
  if (num >= 1_000) return (num / 1_000).toFixed(0) + 'K'
  return num.toLocaleString()
}

function formatCurrency(num: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num)
}

// Status badge component
function StatusBadge({ status }: { status: UsageStatus }) {
  const config = STATUS_CONFIG[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
        config.bgColor,
        config.color
      )}
    >
      {status === 'critical' && <AlertTriangle className="h-3 w-3" />}
      {status === 'warning' && <AlertCircle className="h-3 w-3" />}
      {status === 'healthy' && <CheckCircle className="h-3 w-3" />}
      {status === 'underutilized' && <TrendingDown className="h-3 w-3" />}
      {config.label}
    </span>
  )
}

// Utilization gauge component
function UtilizationGauge({
  percent,
  size = 'md',
}: {
  percent: number
  size?: 'sm' | 'md' | 'lg'
}) {
  const displayPercent = Math.min(percent, 100)

  const getColor = () => {
    if (percent > 100) return 'text-red-500'
    if (percent > 90) return 'text-amber-500'
    if (percent < 50) return 'text-blue-500'
    return 'text-emerald-500'
  }

  const getBgColor = () => {
    if (percent > 100) return 'bg-red-500'
    if (percent > 90) return 'bg-amber-500'
    if (percent < 50) return 'bg-blue-500'
    return 'bg-emerald-500'
  }

  const sizeClasses = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3',
  }

  return (
    <div className="w-full">
      <div className={cn('w-full bg-gray-200 rounded-full overflow-hidden', sizeClasses[size])}>
        <div
          className={cn('h-full rounded-full transition-all', getBgColor())}
          style={{ width: `${Math.min(displayPercent, 100)}%` }}
        />
      </div>
      {size !== 'sm' && (
        <div className={cn('text-xs mt-1 font-medium', getColor())}>
          {parseFloat(percent.toFixed(2))}% utilized
          {percent > 100 && <span className="text-red-600 ml-1">({parseFloat((percent - 100).toFixed(2))}% over)</span>}
        </div>
      )}
    </div>
  )
}

// Calculate average monthly usage from monthlyUsage array
function getAverageMonthlyUsage(account: AccountUsageData): number {
  if (!account.monthlyUsage || account.monthlyUsage.length === 0) return 0
  const total = account.monthlyUsage.reduce((sum, m) => sum + m.totalEvents, 0)
  return Math.floor(total / account.monthlyUsage.length)
}

// Monthly usage chart (horizontal bars)
function MonthlyUsageChart({ account }: { account: AccountUsageData }) {
  const last6Months = account.monthlyUsage?.slice(-6) || []
  const monthlyAverage = account.license?.monthlyAverage || getAverageMonthlyUsage(account)
  const maxEvents = Math.max(
    ...last6Months.map((m) => m.totalEvents),
    monthlyAverage
  )

  if (last6Months.length === 0) {
    return <div className="text-sm text-gray-500 text-center py-4">No monthly data available</div>
  }

  return (
    <div className="space-y-2">
      {last6Months.map((month) => {
        const monthLabel = new Date(month.month + '-01').toLocaleDateString('en-US', {
          month: 'short',
        })
        const percent = (month.totalEvents / maxEvents) * 100
        const vsAverage = monthlyAverage > 0 ? ((month.totalEvents / monthlyAverage) * 100).toFixed(0) : '0'

        return (
          <div key={month.month} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-8">{monthLabel}</span>
            <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden relative">
              {/* License average line */}
              <div
                className="absolute top-0 bottom-0 w-px bg-gray-400 z-10"
                style={{
                  left: `${(monthlyAverage / maxEvents) * 100}%`,
                }}
              />
              {/* Usage bar */}
              <div
                className={cn(
                  'h-full rounded transition-all',
                  month.totalEvents > monthlyAverage
                    ? 'bg-amber-400'
                    : 'bg-emerald-400'
                )}
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className="text-xs text-gray-600 w-16 text-right">
              {formatNumber(month.totalEvents)}
            </span>
            <span
              className={cn(
                'text-xs w-12 text-right font-medium',
                Number(vsAverage) > 100 ? 'text-amber-600' : 'text-gray-400'
              )}
            >
              {vsAverage}%
            </span>
          </div>
        )
      })}
      <div className="flex items-center gap-2 mt-1 pt-1 border-t border-gray-100">
        <span className="text-xs text-gray-400 w-8"></span>
        <div className="flex-1 flex items-center gap-2 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-emerald-400 rounded" />
            <span>Within limit</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-amber-400 rounded" />
            <span>Above average</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-px h-3 bg-gray-400" />
            <span>Monthly avg</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Account card for the grid view
function AccountCard({ account }: { account: AccountUsageData }) {
  const averageMonthlyUsage = getAverageMonthlyUsage(account)
  const yearlyLimit = account.license?.yearlyLimit || 0
  const tierName = account.license?.tier || 'Unknown'

  return (
    <Card
      className={cn(
        'hover:shadow-md transition-shadow',
        account.status === 'critical' && 'ring-2 ring-red-200',
        account.status === 'warning' && 'ring-2 ring-amber-200'
      )}
    >
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-400" />
              <h3 className="font-semibold text-gray-900">{account.accountName}</h3>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-gray-500">#{account.accId}</span>
              <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">
                {tierName}
              </span>
            </div>
          </div>
          <StatusBadge status={account.status as UsageStatus} />
        </div>

        <div className="space-y-3">
          {/* Utilization */}
          <div>
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>YTD Utilization</span>
              <span>{formatNumber(account.yearToDateUsage)} / {formatNumber(yearlyLimit)}</span>
            </div>
            <UtilizationGauge percent={account.utilizationPercent} size="md" />
          </div>

          {/* Key metrics */}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
            <div>
              <div className="text-xs text-gray-500">Avg Monthly</div>
              <div className="font-semibold text-gray-900">
                {formatNumber(averageMonthlyUsage)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Projected Yearly</div>
              <div
                className={cn(
                  'font-semibold',
                  account.projectedOverage > 0 ? 'text-red-600' : 'text-gray-900'
                )}
              >
                {formatNumber(account.projectedYearlyUsage)}
              </div>
            </div>
          </div>

          {/* Warning/Info */}
          {account.projectedOverage > 0 && (
            <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg text-xs text-red-700">
              <AlertTriangle className="h-4 w-4" />
              <span>
                Projected overage: <strong>{formatNumber(account.projectedOverage)}</strong> events
              </span>
            </div>
          )}
          {account.status === 'underutilized' && (
            <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
              <TrendingDown className="h-4 w-4" />
              <span>Using only {account.utilizationPercent}% of license</span>
            </div>
          )}

          {/* View details link */}
          <Link
            to={`/finance/account/${account.accId}`}
            className="flex items-center justify-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 pt-2"
          >
            View Details <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

// Alert/Action item component
function ActionItem({
  icon,
  title,
  description,
  action,
  variant = 'default',
}: {
  icon: React.ReactNode
  title: string
  description: string
  action?: { label: string; href: string }
  variant?: 'default' | 'warning' | 'critical'
}) {
  const variants = {
    default: 'bg-gray-50 border-gray-200',
    warning: 'bg-amber-50 border-amber-200',
    critical: 'bg-red-50 border-red-200',
  }

  return (
    <div className={cn('flex items-start gap-3 p-3 rounded-lg border', variants[variant])}>
      <div className="flex-shrink-0 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 text-sm">{title}</h4>
        <p className="text-xs text-gray-600 mt-0.5">{description}</p>
      </div>
      {action && (
        <Link
          to={action.href}
          className="flex-shrink-0 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
        >
          {action.label}
        </Link>
      )}
    </div>
  )
}

export function FinanceDashboard() {
  const { data, loading, error, refetch } = useAccountUsage()

  const accounts = useMemo(() => data?.accounts || [], [data])
  const summary = useMemo(() => data?.summary || {
    totalAccounts: 0,
    totalYtdEvents: 0,
    totalLicensedEvents: 0,
    overallUtilization: 0,
    healthyCount: 0,
    warningCount: 0,
    criticalCount: 0,
    underutilizedCount: 0,
    projectedOverageRevenue: 0,
    churnRiskAmount: 0,
  }, [data])

  const criticalAccounts = useMemo(() => accounts.filter((a) => a.status === 'critical'), [accounts])
  const warningAccounts = useMemo(() => accounts.filter((a) => a.status === 'warning'), [accounts])
  const underutilizedAccounts = useMemo(() => accounts.filter((a) => a.status === 'underutilized'), [accounts])
  const healthyAccounts = useMemo(() => accounts.filter((a) => a.status === 'healthy'), [accounts])

  if (loading) {
    return (
      <div className="p-6">
        <LoadingState message="Loading finance data..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorState error={error} message="Failed to load finance data" onRetry={refetch} />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="h-7 w-7 text-emerald-600" />
            Finance Dashboard
          </h1>
          <p className="text-gray-500 mt-1">
            License utilization and billing insights across all accounts
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/finance/consumables">
            <Button variant="outline" size="sm" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Consumables Report
            </Button>
          </Link>
          <Link to="/finance/adjustments">
            <Button variant="outline" size="sm" className="gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Usage Adjustments
            </Button>
          </Link>
          <Link to="/finance/balances">
            <Button variant="outline" size="sm" className="gap-2">
              <Wallet className="h-4 w-4" />
              Consumable Balances
            </Button>
          </Link>
          <Button variant="outline" size="sm" className="gap-2">
            <Download className="h-4 w-4" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Critical Alerts */}
      {(criticalAccounts.length > 0 || warningAccounts.length > 0) && (
        <div className="space-y-2">
          {criticalAccounts.map((acc) => {
            const overageRate = acc.license?.overageRate || 10
            const overageCharge = (acc.projectedOverage / 1000) * overageRate
            return (
              <ActionItem
                key={acc.accId}
                icon={<AlertTriangle className="h-5 w-5 text-red-500" />}
                title={`${acc.accountName} - Overage Risk`}
                description={`Projected to exceed license by ${formatNumber(acc.projectedOverage)} events. Potential overage charge: ${formatCurrency(overageCharge)}`}
                action={{ label: 'Review', href: `/finance/account/${acc.accId}` }}
                variant="critical"
              />
            )
          })}
          {warningAccounts.slice(0, 2).map((acc) => (
            <ActionItem
              key={acc.accId}
              icon={<AlertCircle className="h-5 w-5 text-amber-500" />}
              title={`${acc.accountName} - Approaching Limit`}
              description={`Currently at ${acc.utilizationPercent}% utilization. Monitor closely.`}
              action={{ label: 'View', href: `/finance/account/${acc.accId}` }}
              variant="warning"
            />
          ))}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Building2 className="h-4 w-4" />
              <span className="text-sm">Total Accounts</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{summary.totalAccounts}</div>
            <div className="flex items-center gap-4 mt-2 text-xs">
              <span className="text-red-600">{summary.criticalCount + summary.warningCount} at risk</span>
              <span className="text-blue-600">{summary.underutilizedCount} underutilized</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Activity className="h-4 w-4" />
              <span className="text-sm">YTD Usage</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {formatNumber(summary.totalYtdEvents)}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              of {formatNumber(summary.totalLicensedEvents)} licensed
            </div>
          </CardContent>
        </Card>

        <Card className={summary.projectedOverageRevenue > 0 ? 'ring-2 ring-amber-200' : ''}>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <ArrowUpRight className="h-4 w-4" />
              <span className="text-sm">Potential Overage</span>
            </div>
            <div className="text-3xl font-bold text-amber-600">
              {formatCurrency(summary.projectedOverageRevenue)}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              From {criticalAccounts.length} account{criticalAccounts.length !== 1 ? 's' : ''}
            </div>
          </CardContent>
        </Card>

        <Card className={summary.churnRiskAmount > 0 ? 'ring-2 ring-blue-200' : ''}>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <ArrowDownRight className="h-4 w-4" />
              <span className="text-sm">Churn Risk (ARR)</span>
            </div>
            <div className="text-3xl font-bold text-blue-600">
              {formatCurrency(summary.churnRiskAmount)}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {underutilizedAccounts.length} underutilized account{underutilizedAccounts.length !== 1 ? 's' : ''}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account Status Overview */}
      <div className="grid grid-cols-4 gap-4">
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-red-500"
        >
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-red-600">{criticalAccounts.length}</div>
                <div className="text-sm text-gray-600">Overage Risk</div>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-amber-500"
        >
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-amber-600">{warningAccounts.length}</div>
                <div className="text-sm text-gray-600">Warning</div>
              </div>
              <AlertCircle className="h-8 w-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-emerald-500"
        >
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-emerald-600">
                  {healthyAccounts.length}
                </div>
                <div className="text-sm text-gray-600">Healthy</div>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-blue-500"
        >
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {underutilizedAccounts.length}
                </div>
                <div className="text-sm text-gray-600">Underutilized</div>
              </div>
              <TrendingDown className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accounts Grid - Sorted by risk */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Target className="h-5 w-5 text-gray-500" />
            Account License Status
          </h2>
          <Link
            to="/finance/accounts"
            className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
          >
            View All <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Show critical first, then warning, then others */}
          {[...criticalAccounts, ...warningAccounts, ...underutilizedAccounts]
            .slice(0, 6)
            .map((account) => (
              <AccountCard key={account.accId} account={account} />
            ))}
        </div>
      </div>

      {/* Quick Monthly Comparison for Top Accounts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-gray-500" />
            Monthly Usage Trend - Top Accounts by Volume
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {accounts.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-4">No accounts to display</div>
          ) : (
            [...accounts]
              .sort((a, b) => getAverageMonthlyUsage(b) - getAverageMonthlyUsage(a))
              .slice(0, 3)
              .map((account) => (
                <div key={account.accId}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{account.accountName}</span>
                      <StatusBadge status={account.status as UsageStatus} />
                    </div>
                    <span className="text-sm text-gray-500">
                      License: {formatNumber(account.license?.monthlyAverage || 0)}/mo
                    </span>
                  </div>
                  <MonthlyUsageChart account={account} />
                </div>
              ))
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default FinanceDashboard
